/**
 * Request actions for useRequests hook
 *
 * @module @features/requests/hooks/useRequestsActions
 */

import { useCallback } from 'react';
import { useOrgEmployeeRequestsApi } from '@shared/services';
import { useRequestStore } from '../stores';
import {
    toApiAvailabilityPattern,
    toEmployeeRequest,
    toLegacyRequest,
} from '../utils';
import { useOrgEmployeeStore, selectOrgEmployee } from '@store/orgEmployee';
import type { PutAvailabilityPattern } from '@shared/models/requestTypes';
import type {
    Request,
    CreateRequestDto,
    UpdateRequestDto,
    Conflict,
} from '@features/requests/types';

/** Get effective orgEmployeeId */
function getEffectiveOrgEmployeeId(
    orgEmployeeId: string | null,
    orgEmployeeIdFromStore?: string,
): string | null {
    return (orgEmployeeId ?? orgEmployeeIdFromStore)?.trim() ?? null;
}

/**
 * Hook for request actions
 */
export function useRequestsActions(orgEmployeeId: string | null): {
    createRequest: (dto: CreateRequestDto) => Promise<Request>;
    updateRequest: (
        id: string,
        dto: UpdateRequestDto,
    ) => Promise<Request | null>;
    submitRequest: (
        id: string,
    ) => Promise<
        | { success: true; request: Request; conflicts?: Conflict[] }
        | { success: false; error: string; conflicts?: Conflict[] }
    >;
    cancelRequest: (id: string) => Request | null;
    selectRequest: (id: string) => Promise<void>;
    clearSelection: () => void;
    clearError: () => void;
    getRequestById: (id: string) => Request | null;
    setRequests: (requests: Request[]) => void;
    upsertRequest: (request: Request) => void;
    orgEmployeeId: string | null;
    orgEmployee: ReturnType<typeof useOrgEmployeeStore>;
} {
    const {
        get: getRequest,
        update: updateRequestApi,
        submit: submitRequestApi,
    } = useOrgEmployeeRequestsApi();

    const orgEmployee = useOrgEmployeeStore(selectOrgEmployee);

    // Store actions
    const createRequestAction = useRequestStore((state) => state.createRequest);
    const updateRequestAction = useRequestStore((state) => state.updateRequest);
    const submitRequestAction = useRequestStore((state) => state.submitRequest);
    const cancelRequestAction = useRequestStore((state) => state.cancelRequest);
    const selectRequestAction = useRequestStore((state) => state.selectRequest);
    const clearSelectionAction = useRequestStore(
        (state) => state.clearSelection,
    );
    const clearErrorAction = useRequestStore((state) => state.clearError);
    const getRequestByIdAction = useRequestStore(
        (state) => state.getRequestById,
    );
    const setRequests = useRequestStore((state) => state.setRequests);
    const upsertRequest = useRequestStore((state) => state.upsertRequest);

    const createRequest = useCallback(
        (dto: CreateRequestDto): Promise<Request> => {
            if (!orgEmployeeId) {
                return Promise.reject(new Error('OrgEmployee ID is required'));
            }
            try {
                return Promise.resolve(createRequestAction(dto, orgEmployeeId));
            } catch (err) {
                return Promise.reject(err);
            }
        },
        [createRequestAction, orgEmployeeId],
    );

    const updateRequest = useCallback(
        async (id: string, dto: UpdateRequestDto): Promise<Request | null> => {
            if (!orgEmployeeId) return null;

            const existingRequest = getRequestByIdAction(id);
            if (!existingRequest) return null;

            const updatedRequest: Request = {
                ...existingRequest,
                startDate: dto.startDate ?? existingRequest.startDate,
                endDate: dto.endDate ?? existingRequest.endDate,
                notes: dto.notes ?? existingRequest.notes,
                attachments: dto.attachments ?? existingRequest.attachments,
                shiftPreference:
                    dto.shiftPreference ?? existingRequest.shiftPreference,
                excludedDates:
                    dto.excludedDates ?? existingRequest.excludedDates,
                updatedAt: new Date(),
            };

            const pattern: PutAvailabilityPattern =
                toApiAvailabilityPattern(updatedRequest);

            try {
                const effectiveOrgEmployeeId = getEffectiveOrgEmployeeId(
                    orgEmployeeId,
                    orgEmployee?.id,
                );
                if (!effectiveOrgEmployeeId) return null;

                const response = await updateRequestApi(
                    effectiveOrgEmployeeId,
                    id,
                    pattern,
                );

                if (!response) {
                    updateRequestAction(id, dto, orgEmployeeId ?? '');
                    return null;
                }

                const legacyRequest = toLegacyRequest(
                    toEmployeeRequest(response),
                );
                const updatedLocalRequest = updateRequestAction(
                    id,
                    dto,
                    orgEmployeeId ?? '',
                );
                if (!updatedLocalRequest) return null;
                return legacyRequest;
            } catch {
                return null;
            }
        },
        [
            updateRequestAction,
            orgEmployeeId,
            getRequestByIdAction,
            updateRequestApi,
            orgEmployee?.id,
        ],
    );

    const submitRequest = useCallback(
        async (
            id: string,
        ): Promise<
            | { success: true; request: Request; conflicts?: Conflict[] }
            | { success: false; error: string; conflicts?: Conflict[] }
        > => {
            if (!orgEmployeeId) {
                return {
                    success: false,
                    error: 'OrgEmployee ID is required',
                };
            }

            const existingRequest = getRequestByIdAction(id);
            if (!existingRequest) {
                return { success: false, error: 'Søknad ikke funnet' };
            }

            const effectiveOrgEmployeeId = getEffectiveOrgEmployeeId(
                orgEmployeeId,
                orgEmployee?.id,
            );
            if (!effectiveOrgEmployeeId) {
                return { success: false, error: 'Organisasjons-ID er påkrevd' };
            }

            const result = await submitRequestAction(id, orgEmployeeId ?? '');
            if (!result.success) return result;

            try {
                await submitRequestApi(effectiveOrgEmployeeId, id);
                return result;
            } catch (apiError) {
                const errorMessage =
                    apiError instanceof Error
                        ? apiError.message
                        : 'Feil ved innsending';
                return {
                    success: false,
                    error: errorMessage,
                    conflicts: result.conflicts,
                };
            }
        },
        [
            submitRequestAction,
            orgEmployeeId,
            getRequestByIdAction,
            submitRequestApi,
            orgEmployee?.id,
        ],
    );

    const cancelRequest = useCallback(
        (id: string): Request | null => {
            if (!orgEmployeeId) return null;
            return cancelRequestAction(id, orgEmployeeId);
        },
        [cancelRequestAction, orgEmployeeId],
    );

    const selectRequest = useCallback(
        async (id: string): Promise<void> => {
            selectRequestAction(id);
            const effectiveOrgEmployeeId = getEffectiveOrgEmployeeId(
                orgEmployeeId,
                orgEmployee?.id,
            );
            if (effectiveOrgEmployeeId) {
                try {
                    const response = await getRequest(
                        effectiveOrgEmployeeId,
                        id,
                    );
                    const legacyRequest = toLegacyRequest(
                        toEmployeeRequest(response),
                    );
                    upsertRequest(legacyRequest);
                } catch {
                    // Error handled by authenticated client
                }
            }
        },
        [
            selectRequestAction,
            getRequest,
            upsertRequest,
            orgEmployeeId,
            orgEmployee?.id,
        ],
    );

    const clearSelection = useCallback(() => {
        clearSelectionAction();
    }, [clearSelectionAction]);

    const clearError = useCallback(() => {
        clearErrorAction();
    }, [clearErrorAction]);

    const getRequestById = useCallback(
        (id: string): Request | null => getRequestByIdAction(id),
        [getRequestByIdAction],
    );

    return {
        createRequest,
        updateRequest,
        submitRequest,
        cancelRequest,
        selectRequest,
        clearSelection,
        clearError,
        getRequestById,
        setRequests,
        upsertRequest,
        orgEmployeeId,
        orgEmployee,
    };
}

export type { Request, CreateRequestDto, UpdateRequestDto, Conflict };
